import dayjs from "dayjs";

export const VIEW_HOURS = 0;
export const MIN_CEILING = 125;

export const currency = (v) => `$${Number(v || 0).toLocaleString()}`;
export const decimal = (v) => Math.round((v || 0) * 100) / 100;

export const getWeekGoal = (week, view) =>
  view === VIEW_HOURS
    ? [week.waxHourGoal, week.revenueGoal]
    : [week.revenueGoal, week.waxHourGoal];

export const getDailyGoal = (week, view) =>
  view === VIEW_HOURS
    ? [week.dailyWaxHourGoal, week.dailyRevenueGoal]
    : [week.dailyRevenueGoal, week.dailyWaxHourGoal];

export const getValue = (el, view) =>
  view === VIEW_HOURS ? [el.hours, el.revenue] : [el.revenue, el.hours];

export const getWeekTotals = (week) => ({
  hours: week.elements.reduce((s, e) => s + (e.hours || 0), 0),
  revenue: week.elements.reduce((s, e) => s + (e.revenue || 0), 0),
});

export const getCeiling = (weeks, view) => {
  const maxPct = weeks.reduce((max, week) => {
    const [weeklyGoal] = getWeekGoal(week, view);
    if (!weeklyGoal) return max;
    const total = week.elements.reduce(
      (sum, e) => sum + getValue(e, view)[0],
      0,
    );
    return Math.max(max, (total / weeklyGoal) * 100);
  }, 0);
  return Math.max(Math.ceil(maxPct / 25) * 25, MIN_CEILING);
};

const getWeekCapacity = (week) => {
  const totals = getWeekTotals(week);
  const hourCap = (week.waxHourGoal || 0) - totals.hours;
  const revCap = (week.revenueGoal || 0) - totals.revenue;
  return { hourCap, revCap, combined: hourCap + revCap };
};

export const getSuggestions = (weeks) => {
  const state = structuredClone(weeks);
  const movedIds = new Set();

  const overloaded = state.filter((w) => {
    const t = getWeekTotals(w);
    return t.hours > (w.waxHourGoal || 0) && t.revenue > (w.revenueGoal || 0);
  });

  for (const week of overloaded) {
    const hourGoal = week.waxHourGoal || 0;
    const revGoal = week.revenueGoal || 0;

    const candidates = [...week.elements]
      .map((e, idx) => ({
        ...e,
        idx,
        combined: (e.hours || 0) + (e.revenue || 0),
      }))
      .filter((e) => !e.started)
      .sort((a, b) => a.combined - b.combined);

    const toRemove = [];

    for (const wo of candidates) {
      const t = getWeekTotals(week);
      if (t.hours <= hourGoal && t.revenue <= revGoal) break;

      const destinations = state
        .filter((w) => w.id !== week.id)
        .map((w) => ({ week: w, ...getWeekCapacity(w) }))
        .filter(
          (d) =>
            d.hourCap >= (wo.hours || 0) * 0.5 &&
            d.revCap >= (wo.revenue || 0) * 0.5,
        )
        .sort((a, b) => b.combined - a.combined);

      if (!destinations.length) continue;

      destinations[0].week.elements.push(wo);
      toRemove.push(wo.idx);
      movedIds.add(String(wo.id));
    }

    toRemove
      .sort((a, b) => b - a)
      .forEach((idx) => week.elements.splice(idx, 1));
  }

  return { state, movedIds };
};

export const getAltMetric = ({
  elements = [],
  view = 0,
  ceiling = 125,
  weeklyHourGoal = 0,
  weeklyRevenueGoal = 0,
}) => {
  const isHours = view === VIEW_HOURS;
  const altTotal = elements.reduce(
    (sum, e) => sum + (isHours ? e.revenue || 0 : e.hours || 0),
    0,
  );
  const altGoal = isHours ? weeklyRevenueGoal : weeklyHourGoal;
  const altPct = altGoal ? (altTotal / altGoal) * 100 : 0;
  return {
    altGoal,
    altPct,
    altPosition: (altPct / ceiling) * 100,
    altLabel: isHours ? "Rev" : "Hrs",
  };
};

export const isOverBar = (e) =>
  !!e.target.closest?.(".HubDraggable-root, .HubDroppable-box");

const resolveTargetRef = (weeks, update) => {
  if (!update?.destination) return null;
  const { droppableId, index } = update.destination;
  const week = weeks.find((w) => String(w.id) === String(droppableId));
  if (!week) return null;
  const elements = week.elements || [];
  return index > 0 ? elements[index - 1] : elements[0];
};

export const getTargetDate = (weeks, update) => {
  const ref = resolveTargetRef(weeks, update);
  if (!ref?.startDate) return null;
  return dayjs(ref.startDate).format("ddd, MMM D");
};

export const getTargetStartDate = (weeks, update) => {
  const ref = resolveTargetRef(weeks, update);
  if (!ref?.startDate) return null;
  return dayjs(ref.startDate).toISOString();
};
